"use client"

import Link from "next/link"
import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import { CalculatorCtaBanner } from "@/components/tools/calculator-closing-cta"
import { ChevronRightSmIcon } from "@/components/ui/icons"
import { useCountry } from "@/components/layout/country-provider"
import { healthToolsFor } from "@/lib/health-tools"
import { toolsKeys as k } from "@/i18n/tools-keys"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { cn } from "@/lib/utils"

const CONTAINER = "mx-auto max-w-[1440px] px-4 md:px-10"
const PROSE = "mx-auto max-w-[760px]"

/**
 * `/tools` — the index of the free health calculators.
 *
 * The card list comes from `healthToolsFor(country, locale)`, so it holds only
 * tools this market has AND this locale has been translated into. A tool
 * missing from one of those is absent rather than linked, because
 * `dynamicParams = false` on each calculator makes a wrong-locale link a hard
 * 404 on the one page whose job is to send readers to them.
 */
export default function ToolsClient() {
  const t = useTranslations("tools")
  const tt = useTranslations("healthTools")
  const lp = useLocalePath()
  const locale = useLocale()
  const { code: country } = useCountry()

  const tools = healthToolsFor(country, locale)

  return (
    <>
      {/* ─────────────── Hero ─────────────── */}
      <section className="pt-8 pb-12 md:pt-12 md:pb-16">
        <div className={CONTAINER}>
          <div className={PROSE}>
            <h1 className="type-display-lg text-balance text-neutral-900">
              {t(k.heading)}
            </h1>
            <p className="type-body-lg mt-5 text-neutral-600">
              {t(k.subheading)}
            </p>
          </div>
        </div>
      </section>

      {/* ─────────────── The tools ─────────────── */}
      {tools.length > 0 && (
        <section className="pb-16 md:pb-24">
          <div className={CONTAINER}>
            <ul
              data-testid="tools-list"
              className="mx-auto grid max-w-[760px] grid-cols-1 gap-4 sm:grid-cols-2"
            >
              {tools.map((tool) => (
                <li key={tool.id}>
                  <Link
                    href={lp(tool.path)}
                    data-testid={`tools-card-${tool.id}`}
                    className="group focus-visible:ring-aubergine-400 block h-full rounded-[16px] bg-white p-6 transition-shadow hover:shadow-[0_2px_16px_rgba(0,0,0,0.06)] focus-visible:ring-2 focus-visible:outline-none"
                  >
                    <span className="type-title-md flex items-center gap-1 text-neutral-900">
                      {tt(tool.titleKey)}
                      <ChevronRightSmIcon
                        width={16}
                        height={16}
                        className="text-neutral-400 transition-transform duration-200 ease-out group-hover:translate-x-1"
                      />
                    </span>
                    <span className="type-body-md mt-2 block text-neutral-600">
                      {tt(tool.descKey)}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* ─────────────── Closing CTA ─────────────── */}
      <CalculatorCtaBanner
        tool="tools"
        title={t(k.ctaHeading)}
        subtitle={t(k.ctaBody)}
        primaryLabel={t(k.ctaPrimary)}
        secondaryLabel={t(k.ctaSecondary)}
      />

      {/* ─────────────── Medical disclaimer ─────────────── */}
      <section className="pt-10 pb-16">
        <div className={CONTAINER}>
          <p className={cn(PROSE, "type-body-sm text-neutral-500")}>
            {t(k.disclaimer)}
          </p>
        </div>
      </section>
    </>
  )
}
